import React, { Component, createRef } from "react";
import { StyleSheet, View, TextInput, Text, TouchableOpacity, Platform, InputAccessoryView, Alert } from "react-native";
import { isMobileWeb, getNoAutofillProps, noAutofillRef } from "../../utils/mobileWeb";
import { connect } from "react-redux";
import { searchItems } from "../../actions/records";
import { getMyCustomers, getMySuppliers } from "../../actions/submissions";
import DropDownLocations from "../widgets/dropdownLocations";
import colors from "../../constants/colors";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Swal from "sweetalert2";
import BarcodeScannerComponent from "../widgets/BarCodeScannerComponent";
import KeyboardBar from "../widgets/KeyboardBar";
import { inputFocus } from "../../utils";
import {
    findLocationInList,
    locationListIncludesTerm,
    parseScannedValue,
    renderLocationLabel,
} from "../../utils/locationScan";

class SearchInventoryLocations extends Component {
    inputRef = createRef();
    _mounted = false;
    _handlingScan = false;

    constructor(props) {
        super(props);
        this._interval = null;
        this._resolveTimer = null;
        const useScanner = props.isShipmentByLocation && props.showLocationsSearch && props.defaultLabel === "location";
        this.state = {
            value: (props.defaultLabel == "toLocation" || props.showLocationsSearch) ? renderLocationLabel(props.initial) : typeof props.initial == "string" ? props.initial : "",
            locationSelected: props.initial && typeof props.initial == "object" ? props.initial : [],
            height: 30,
            items: props.options ? props.options : [],
            filtered: props.options ? props.options : [],
            wasEdited: false,
            openList: false,
            scan: false,
            scanerCanceled: false,
            softFocus: useScanner && props.scannerInput ? false : true,
            inputAccessoryViewID: useScanner ? `location-${props.id}-${props.position}` : undefined,
        };
    }
    componentDidMount() {
        this._mounted = true;
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.openBarCodeScanner != this.props.openBarCodeScanner &&
            nextProps.openBarCodeScanner &&
            this.useScannerField()
        ) {
            this.showScanner(true);
        }
        if (nextProps?.options != this.props.options) {
            this.setState({
                items: nextProps.options || [],
                filtered: nextProps.options || [],
            });
        }
        if (nextProps.position != this.props.position) {
            this.setState({
                scan: false,
                wasEdited: false,
                openList: false,
                filtered: [],
                scanerCanceled: false,
            });
        }
        if (nextProps?.initial != this.props.initial) {
            if (typeof nextProps?.initial == "object") {
                if (this.props.defaultLabel == "toLocation" || this.props.showLocationsSearch) {
                    this.setState({
                        value: renderLocationLabel(nextProps.initial),
                        wasEdited: false,
                        openList: false,
                        filtered: [],
                    });
                } else {
                    this.setState({ locationSelected: nextProps?.initial });
                }
            } else if (typeof nextProps?.initial == "string") {
                this.setState({ value: nextProps?.initial });
            }
        }
        if (nextProps.closeSearch != this.props.closeSearch) {
            this.setState({ filtered: [], wasEdited: false });
        }
    }
    componentWillUnmount() {
        this._mounted = false;
        if (this._interval) clearTimeout(this._interval);
        if (this._resolveTimer) clearTimeout(this._resolveTimer);
    }

    getLocationOptions() {
        const { options } = this.props;
        const { items } = this.state;
        if (Array.isArray(options) && options.length) return options;
        if (Array.isArray(items) && items.length) return items;
        return [];
    }

    shouldAutoOpenCameraOnFocus() {
        const { scannerInput, isShipmentByLocation, showLocationsSearch, defaultLabel } = this.props;
        /*
         * Shipment-by-location table rows: never auto-open the camera on focus.
         * Row 1 often already has a value (so camera stayed off); empty row 2 used to
         * auto-open the camera on Zebra / mobile web and crash the app. Use the hardware
         * wedge or the KeyboardBar scan button instead.
         */
        if (isShipmentByLocation && showLocationsSearch && defaultLabel === "location") {
            return false;
        }
        if (!scannerInput || !this.scannerFieldActive()) return false;
        if (Platform.OS === "android") return false;
        return Platform.OS === "ios" || (Platform.OS === "web" && isMobileWeb());
    }

    showScanAlert(message) {
        if (Platform.OS === "web") {
            Swal.fire({
                text: message,
                confirmButtonText: "Accept",
                confirmButtonColor: colors.orange,
                backdrop: false,
            });
            return;
        }
        Alert.alert("", message, [{ text: "Accept" }]);
    }

    useScannerField() {
        const { isShipmentByLocation, showLocationsSearch, defaultLabel } = this.props;
        return !!(isShipmentByLocation && showLocationsSearch && defaultLabel === "location");
    }

    scannerFieldActive() {
        const { showScanner, scannerInput } = this.props;
        return showScanner || (this.useScannerField() && scannerInput);
    }

    showScanner(value) {
        this.setState({ scan: value, scanerCanceled: value ? false : this.state.scanerCanceled });
    }

    showScannerInput() {
        const { setFormScroll } = this.props;
        if (!this.state.scanerCanceled) {
            if (setFormScroll) setFormScroll(false);
            setTimeout(() => {
                this.showScanner(true);
                setTimeout(() => {
                    if (setFormScroll) setFormScroll(true);
                }, 300);
            }, 500);
        } else {
            this.setState({ scanerCanceled: false });
        }
    }

    findLocationByScan(data) {
        return findLocationInList(data, this.getLocationOptions());
    }

    tryResolveScannedValue(raw, { updateForm = true } = {}) {
        const clean = parseScannedValue(raw).replace(/[\x00-\x1F\x7F]/g, "").trim();
        if (!clean) return false;
        const match = this.findLocationByScan(clean);
        if (match) {
            this.selectLocation(match, { skipForm: !updateForm });
            return true;
        }
        return false;
    }

    scheduleResolveInput(raw) {
        const { isShipmentByLocation, scannerInput } = this.props;
        const useScanner = this.useScannerField();
        if (!useScanner) return;

        if (this._resolveTimer) clearTimeout(this._resolveTimer);

        const clean = parseScannedValue(raw).replace(/[\x00-\x1F\x7F]/g, "").trim();
        const looksComplete =
            /[\r\n]/.test(parseScannedValue(raw)) ||
            (scannerInput && clean.length >= 2 && this.findLocationByScan(clean));

        if (looksComplete && this.tryResolveScannedValue(raw)) {
            return;
        }

        const delay = scannerInput ? 120 : 400;
        this._resolveTimer = setTimeout(() => {
            this._resolveTimer = null;
            if (!this._mounted) return;
            if (this.tryResolveScannedValue(raw)) return;
            if (isShipmentByLocation) {
                this.searchItems(clean, false);
            }
        }, delay);
    }

    finishScanProcessing(data) {
        if (!this._mounted || !data) return;
        try {
            var location = this.findLocationByScan(data);
            if (location) {
                this.selectLocation(location);
            } else {
                this.showScanAlert("Sorry, the scanned location does not exist on inventory");
            }
        } catch (err) {
            console.warn("SearchInventoryLocations finishScanProcessing:", err);
            this.showScanAlert("Unable to process the scanned location. Please try again or search manually.");
        }
    }

    handleBarCodeScanned(raw) {
        const data = parseScannedValue(raw);
        if (!this._mounted || this._handlingScan) return;
        this._handlingScan = true;
        this.setState({ scan: false });
        const settleMs = Platform.OS === "web" ? 450 : 250;
        setTimeout(() => {
            this._handlingScan = false;
            if (!this._mounted) return;
            this.finishScanProcessing(data);
        }, settleMs);
    }
    searchItems(val, focused) {
        const items = this.getLocationOptions();
        const { isShipmentByLocation } = this.props;
        var search = val;
        if (this._interval) clearTimeout(this._interval);
        if (search) {
            search = search.toLowerCase().trim();
            if (isShipmentByLocation) {
                var resolved = this.findLocationByScan(search);
                if (resolved) {
                    this.selectLocation(resolved);
                    return;
                }
            }
            var filtered = items.filter((item) => locationListIncludesTerm([item], search));
            if (isShipmentByLocation) filtered = filtered.slice(0, 10);
            this.setState({ filtered: filtered, openList: true });

        } else {
            var filtered = [];
            if (isShipmentByLocation) filtered = items.slice(0, 10);
            else filtered = items;
            this.setState({ filtered, wasEdited: focused ? true : false, openList: true });
        }
    }
    noFoundLocation(search) {
        const { onSelectItem } = this.props;
        search = search.toLowerCase().trim();
        var filtered = this.state.items.filter((item) => {
            var completeLocation = "";
            if (item?.site?.locationName) {
                completeLocation += item.site.locationName;
            }
            if (item?.zone?.locationName) {
                completeLocation += "-" + item.zone.locationName;
            }
            if (item?.aisle?.locationName) {
                completeLocation += "-" + item.aisle.locationName;
            }
            if (item?.rackBay?.locationName) {
                completeLocation += "-" + item.rackBay.locationName;
            }
            if (item?.shelfLevel?.locationName) {
                completeLocation += "-" + item.shelfLevel.locationName;
            }
            if (item?.bin?.locationName) {
                completeLocation += "-" + item.bin.locationName;
            }
            completeLocation = completeLocation.toLowerCase().trim();
            return completeLocation.includes(search);
        });
        if (filtered.length <= 0)
            Swal.fire({
                text: "This location does not exist. A new main location will be created automatically on sending the form, but it must be reorganized by an administrator. This is not recommended. Do you want to proceed?",
                confirmButtonText: "Yes",
                cancelButtonText: "No",
                showCancelButton: true,
                confirmButtonColor: colors.orange,
                backdrop: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    this.setState({ value: search, filtered: [], openList: false });
                    onSelectItem(search);
                } else {
                    this.setState({ value: "", filtered: [], openList: false });
                    onSelectItem("");
                }
            });
    }
    selectLocation(item, { skipForm = false } = {}) {
        const { onSelectItem } = this.props;
        const label = renderLocationLabel(item);
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }
        this.setState({
            value: label,
            wasEdited: false,
            openList: false,
            filtered: [],
            scan: false,
        }, () => {
            if (skipForm || !onSelectItem || !this._mounted) return;
            setTimeout(() => {
                if (!this._mounted) return;
                onSelectItem([item]);
            }, 50);
        });
    }

    renderLocation(location) {
        return renderLocationLabel(location);
    }
    render() {
        const { value, items, wasEdited, filtered, openList, scan, scanerCanceled, softFocus, inputAccessoryViewID } = this.state;
        const { defaultComponent, onSelectItem, placeholder, customers, width, isRecipe, openDropDown, backgroundColor, isLast, defaultLabel, isDropDownOpened, resetDropDown, onFocus, setShowTextBar, showLocationsSearch, isShipmentByLocation, handleFocusOnElement, setFormScroll, id, position, scannerInput, showScanner } = this.props;
        const useScanner = this.useScannerField();
        const scannerFieldForInput = this.scannerFieldActive();

        return <View style={{ ...styles.container, paddingTop: width ? 0 : 5 }}>
            <TextInput
                {...getNoAutofillProps()}
                ref={noAutofillRef(this.inputRef)}
                value={value}
                editable={!defaultComponent && !(useScanner && scannerInput && scannerFieldForInput && !value && !scanerCanceled && Platform.OS === "ios")}
                showSoftInputOnFocus={Platform.OS === "ios" ? true : softFocus}
                onPress={() => {
                    if (this.shouldAutoOpenCameraOnFocus() && useScanner && !value && !scanerCanceled) {
                        this.showScannerInput();
                    }
                    if (Platform.OS === "web" && isMobileWeb() && !defaultComponent && onFocus) {
                        onFocus();
                    }
                }}
                onFocus={() => {
                    if (this.shouldAutoOpenCameraOnFocus() && useScanner && !value && !scanerCanceled) {
                        this.showScannerInput();
                        if (Platform.OS !== "ios" && !softFocus) {
                            this.setState({ softFocus: true });
                            return;
                        }
                    }
                    if (useScanner && Platform.OS === "android" && scannerInput && scannerFieldForInput && !softFocus) {
                        this.setState({ softFocus: true });
                    }
                    const skipParentScannerReopen = useScanner && scannerInput && scannerFieldForInput && !value && scanerCanceled;
                    if (onFocus && !skipParentScannerReopen) {
                        onFocus();
                    } else if (skipParentScannerReopen) {
                        setTimeout(() => {
                            if (handleFocusOnElement && this.inputRef?.current) {
                                handleFocusOnElement(this.inputRef.current);
                            }
                        }, 300);
                    }
                    if (!defaultComponent && !value && !(useScanner && scannerFieldForInput)) {
                        this.searchItems("", true);
                    }
                    if (useScanner && setShowTextBar && isMobileWeb()) {
                        setShowTextBar(id, position, [], null, null, null, null, false, true);
                    } else if (setShowTextBar && (isMobileWeb() || Platform.OS === "android")) {
                        setShowTextBar(null, null, null, null, null, null, null, false);
                    }
                }}
                onContentSizeChange={(event) => {
                    if (event.nativeEvent.contentSize.height == 0) {
                        if (this.props.placeholder == '') {
                            this.setState({ height: 30 })
                        }
                        this.setState({ height: event.nativeEvent.contentSize.height });
                    } else {
                        setTimeout(() => {
                            this.setState({ height: event.nativeEvent.contentSize.height });
                        }, 5)
                    }
                }}
                onBlur={() => {
                    if (useScanner && scannerInput && scannerFieldForInput) {
                        this.setState({ scanerCanceled: false });
                        if (softFocus) this.setState({ softFocus: false });
                    }
                    if (useScanner && setShowTextBar && isMobileWeb()) {
                        setShowTextBar(null, null, null, null, null, null, null, false);
                    }
                    if (useScanner && value && this.tryResolveScannedValue(value)) {
                        return;
                    }
                    if (isShipmentByLocation && this.useScannerField() && value && wasEdited) {
                        this.showScanAlert("Sorry, the scanned location does not exist on inventory");
                        this.setState({ value: "", wasEdited: false, filtered: [], openList: false });
                        return;
                    }
                    if (
                        (defaultLabel == "toLocation" || showLocationsSearch) &&
                        value &&
                        wasEdited &&
                        filtered?.length <= 0
                    ) {
                        this.noFoundLocation(value);
                    }
                }}
                inputAccessoryViewID={useScanner ? inputAccessoryViewID : undefined}
                style={{
                    ...styles.input,
                    borderColor: "#e7e7e7",
                    borderRadius: 5,
                    borderWidth: 1,
                    color: "#424242",
                    fontFamily: "Roboto",
                    fontSize: 20,
                    fontWeight: "400",
                    padding: 5,
                    height: this.state.height,
                    fontStyle: this.state.value ? this.state.value.length == 0 ? 'italic' : 'normal' : 'normal',
                    overflow: 'hidden'
                }}
                multiline={true}
                placeholder={placeholder}
                placeholderTextColor={'rgb(0, 47, 108)'}
                onChangeText={(val) => {
                    const cleanVal = parseScannedValue(val).replace(/[\x00-\x1F\x7F]/g, "").trim();
                    if (cleanVal == '' && this.props.placeholder == '') {
                        this.setState({ value: cleanVal, height: 30, wasEdited: true });
                    } else {
                        this.setState({ value: cleanVal, wasEdited: true });
                    }

                    if (useScanner && this.scannerFieldActive()) {
                        this.scheduleResolveInput(cleanVal);
                        return;
                    }

                    if (this._interval) clearTimeout(this._interval);
                    this._interval = setTimeout(() => {
                        this._interval = null;
                        this.searchItems(cleanVal, false);
                    }, 500);
                    onSelectItem(cleanVal);
                }}
            />
            {Platform.OS === "ios" && useScanner && (
                <InputAccessoryView nativeID={inputAccessoryViewID}>
                    <KeyboardBar
                        abcOptionPress={null}
                        scanOptionPress={() => this.showScanner(true)}
                    />
                </InputAccessoryView>
            )}
            {
                items?.length > 0 && defaultLabel != "toLocation" && !showLocationsSearch &&
                <DropDownLocations
                    initialOption={this.state.locationSelected}
                    updated={this.state.locationSelected}
                    options={filtered}
                    defaultComponent={defaultComponent}
                    openList={openList}
                    allLabel={"All Locations"}
                    onSelect={(option) => {
                        onSelectItem(option)
                    }}
                    width={width}
                    isRecipe={isRecipe}
                    openDropDown={openDropDown}
                    backgroundColor={backgroundColor}
                    isLast={isLast}
                    isDropDownOpened={isDropDownOpened}
                    resetDropDown={resetDropDown}
                    showScanner={showScanner}
                />
            }
            {
                filtered?.length > 0 && (defaultLabel == "toLocation" || showLocationsSearch) && wasEdited &&
                <View style={styles.listContainer}>
                    {filtered.map((item, idx) => (
                        <TouchableOpacity
                            key={idx}
                            style={styles.row}
                            onPress={() => this.selectLocation(item)}
                        >
                            <View style={styles.rowText}>
                                <Text style={styles.rowLabel}>{this.renderLocation(item)}</Text>
                            </View>
                            <TouchableOpacity
                                style={styles.rowBtn}
                                onPress={() => this.selectLocation(item)}
                            >
                                <MaterialCommunityIcons name="plus" size={22} color={colors.selectSearch} />
                            </TouchableOpacity>
                        </TouchableOpacity>
                    ))}
                </View>
            }
            {
                (value && filtered?.length <= 0 && wasEdited) ? <View>
                    <Text>No locations were found</Text>
                </View> : null
            }
            {scan && useScanner ? (
                <View style={styles.scannerOverlay}>
                    <BarcodeScannerComponent
                        onBarCodeScanned={(data) => this.handleBarCodeScanned(data)}
                        closeScanner={() => {
                            this.showScanner(false);
                            if (scannerInput && scannerFieldForInput) {
                                this.setState({ scanerCanceled: true }, () => {
                                    setTimeout(() => {
                                        if (this._mounted) inputFocus(this.inputRef);
                                    }, 500);
                                });
                            }
                        }}
                    />
                </View>
            ) : null}
        </View>
    }
}

export default connect((state, ownProps = {}) => ({
    isLoading: state.forms.getIn(["form", "status"]) === "loading",
    company_id: state.user.get("company_id"),
}), (dispatch, props) => ({
    searchItems: (formid, saveAsTemplate, saveAsForm, callback) =>
        dispatch(searchItems(formid, saveAsTemplate, saveAsForm, callback)),
    getMyCustomers: (data, callback) =>
        dispatch(getMyCustomers(data, callback)),
    getMySuppliers: (id, page, filter, callback) =>
        dispatch(getMySuppliers(id, page, filter, callback)),
}))(SearchInventoryLocations);

const styles = StyleSheet.create({
    container: {
        flexDirection: "column",
        paddingTop: 5,
        flex: 1,
    },
    input: {
        borderColor: "#e7e7e7",
        borderRadius: 5,
        borderWidth: 1,
        color: "#424242",
        fontFamily: "Roboto",
        fontSize: 22,
        fontWeight: "400",
        paddingLeft: 10,
    },
    label: {
        fontSize: 20,
        color: '#939598',
        fontWeight: '300',
        fontFamily: 'Roboto',

    },
    listContainer: {
        paddingTop: 4,
        width: "100%",
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        paddingVertical: 6,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomColor: "#e7e7e7",
    },
    rowText: {
        flex: 1,
        paddingRight: 8,
    },
    rowLabel: {
        fontFamily: "Roboto",
        fontSize: 18,
        color: "#424242",
    },
    rowBtn: {
        borderRadius: 4,
        padding: 6,
        borderWidth: 1,
        borderColor: colors.selectSearch,
        alignItems: "center",
        justifyContent: "center",
        minWidth: 36,
        minHeight: 36,
    },
    scannerOverlay: {
        ...(Platform.OS === "web"
            ? { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000 }
            : { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000, elevation: 20 }),
        backgroundColor: "#000",
    },
});